import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { APP_NAME } from "@/lib/brand";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Wallet,
  ArrowUpCircle,
  ArrowDownCircle,
  Loader2,
  TrendingDown,
  Coins,
  Zap,
  ExternalLink,
  UserCircle,
  Key,
} from "lucide-react";

interface ClientWallet {
  id: string;
  client_id: string;
  balance: number;
  base_rate: number;
  updated_at: string;
}

interface WalletTransaction {
  id: string;
  client_id: string;
  amount: number;
  type: string;
  description: string | null;
  tokens_used: number | null;
  conversation_id: string | null;
  created_by: string | null;
  key_source: string | null;
  created_at: string;
  // populated client-side
  conversation_title?: string;
  user_full_name?: string;
}

interface WalletLedgerProps {
  employeeFilter?: { profileId: string; profileName: string } | null;
  onClearFilter?: () => void;
}

interface EmployeeOption {
  id: string;
  full_name: string;
}

const WalletLedger = ({ employeeFilter, onClearFilter }: WalletLedgerProps) => {
  const { profile } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const [wallet, setWallet] = useState<ClientWallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<EmployeeOption[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<string>("all");

  // Fetch employees for the dropdown
  useEffect(() => {
    if (!profile?.client_id) return;
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, full_name")
        .eq("client_id", profile.client_id)
        .order("full_name");
      if (data) {
        setEmployees(
          (data as EmployeeOption[]).filter((e) => e.full_name)
        );
      }
    })();
  }, [profile?.client_id]);

  // Sync external employeeFilter prop with the dropdown
  useEffect(() => {
    if (employeeFilter?.profileId) {
      setSelectedEmployee(employeeFilter.profileId);
    }
  }, [employeeFilter?.profileId]);

  // Determine the active profile id for filtering
  const activeProfileId =
    selectedEmployee !== "all" ? selectedEmployee : employeeFilter?.profileId ?? null;

  const fetchData = useCallback(async () => {
    if (!profile?.client_id) {
      setLoading(false);
      return;
    }

    setLoading(true);

    let txQuery = supabase
      .from("wallet_transactions")
      .select("*")
      .eq("client_id", profile.client_id)
      .order("created_at", { ascending: false })
      .limit(200);

    if (activeProfileId) {
      txQuery = txQuery.eq("profile_id", activeProfileId);
    }

    const [walletRes, txRes] = await Promise.all([
      supabase
        .from("client_wallet")
        .select("*")
        .eq("client_id", profile.client_id)
        .single(),
      txQuery,
    ]);

    if (walletRes.error && walletRes.error.code !== "PGRST116") {
      toast({ title: "Error loading wallet", description: walletRes.error.message, variant: "destructive" });
    } else {
      setWallet((walletRes.data as ClientWallet) ?? null);
    }

    if (txRes.error) {
      toast({ title: "Error loading transactions", description: txRes.error.message, variant: "destructive" });
      setLoading(false);
      return;
    }

    const txList = (txRes.data as WalletTransaction[]) ?? [];

    // ── Resolve conversation titles + user_ids ──────────────────────────────
    const convIds = [...new Set(txList.map((t) => t.conversation_id).filter(Boolean))] as string[];
    let convMap: Record<string, { title: string; user_id: string }> = {};
    if (convIds.length > 0) {
      const { data: convData } = await supabase
        .from("conversations")
        .select("id, title, user_id")
        .in("id", convIds);
      if (convData) {
        convMap = Object.fromEntries(
          (convData as { id: string; title: string; user_id: string }[]).map((c) => [
            c.id,
            { title: c.title, user_id: c.user_id },
          ])
        );
      }
    }

    // ── Batch-fetch profile full names ──────────────────────────────────────
    // Credits: created_by; Debits: conversation owner (user_id)
    const profileIds = [
      ...new Set([
        ...txList.map((t) => t.created_by).filter(Boolean),
        ...Object.values(convMap).map((c) => c.user_id).filter(Boolean),
      ]),
    ] as string[];

    let profileMap: Record<string, string> = {};
    if (profileIds.length > 0) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", profileIds);
      if (profileData) {
        profileMap = Object.fromEntries(
          (profileData as { id: string; full_name: string | null }[]).map((p) => [
            p.id,
            p.full_name || p.id,
          ])
        );
      }
    }

    setTransactions(
      txList.map((tx) => {
        const conv = tx.conversation_id ? convMap[tx.conversation_id] : undefined;
        const userId = tx.type === "credit" ? tx.created_by : conv?.user_id ?? null;
        return {
          ...tx,
          conversation_title: conv?.title,
          user_full_name: userId ? profileMap[userId] : undefined,
        };
      })
    );

    setLoading(false);
  }, [profile?.client_id, activeProfileId, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ── Derived stats ──────────────────────────────────────────────────────────

  const totalCredits = transactions
    .filter((t) => t.type === "credit")
    .reduce((sum, t) => sum + t.amount, 0);

  const totalDebits = transactions
    .filter((t) => t.type === "debit")
    .reduce((sum, t) => sum + Math.abs(t.amount), 0);

  const totalTokens = transactions
    .filter((t) => t.tokens_used != null)
    .reduce((sum, t) => sum + (t.tokens_used ?? 0), 0);

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!profile?.client_id) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-4 px-4">
        <Wallet className="h-12 w-12 text-muted-foreground" />
        <p className="text-muted-foreground">No client associated with your account.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 md:p-6 overflow-y-auto">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">Wallet & Ledger</h1>
          <p className="text-sm text-muted-foreground">
            Your organization's token credit balance and transaction history.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <UserCircle className="h-4 w-4 text-muted-foreground shrink-0" />
          <Select
            value={selectedEmployee}
            onValueChange={(value) => {
              setSelectedEmployee(value);
              if (value === "all" && onClearFilter) onClearFilter();
            }}
          >
            <SelectTrigger className="w-[220px]">
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map((emp) => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.full_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Current Balance</CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div
              className={`text-2xl font-bold font-mono ${
                (wallet?.balance ?? 0) < 0 ? "text-destructive" : ""
              }`}
            >
              {(wallet?.balance ?? 0).toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">points</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Credits</CardTitle>
            <ArrowUpCircle className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-green-700">
              +{totalCredits.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">points received</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Debits</CardTitle>
            <TrendingDown className="h-4 w-4 text-destructive" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono text-destructive">
              -{totalDebits.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">points consumed</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tokens Used</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold font-mono">
              {totalTokens >= 1_000_000
                ? `${(totalTokens / 1_000_000).toFixed(2)}M`
                : totalTokens >= 1_000
                ? `${(totalTokens / 1_000).toFixed(1)}K`
                : totalTokens.toLocaleString()}
            </div>
            <p className="text-xs text-muted-foreground">
              @ {Number(wallet?.base_rate ?? 500).toLocaleString()} pts/1M tokens
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Ledger table */}
      <div>
        <h2 className="text-lg font-semibold mb-3 flex items-center gap-2">
          <Coins className="h-5 w-5" />
          Transaction History
        </h2>

        {transactions.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-12">
            <Coins className="h-10 w-10 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">No transactions yet.</p>
          </Card>
        ) : (
          <Card className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Conversation</TableHead>
                  <TableHead>Key Source</TableHead>
                  <TableHead className="text-right">Tokens Used</TableHead>
                  <TableHead className="text-right">Amount (pts)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                      {new Date(tx.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {tx.type === "credit" ? (
                        <Badge variant="secondary" className="gap-1 text-green-700 bg-green-50">
                          <ArrowUpCircle className="h-3 w-3" />
                          Credit
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="gap-1 text-red-700 bg-red-50">
                          <ArrowDownCircle className="h-3 w-3" />
                          Debit
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      <span>{tx.description ?? "—"}</span>
                      {tx.conversation_title && (
                        <span className="text-muted-foreground"> — {tx.conversation_title}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {tx.user_full_name ?? <span className="text-muted-foreground">—</span>}
                    </TableCell>
                    <TableCell>
                      {tx.conversation_id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 gap-1.5 px-2 text-xs font-mono text-muted-foreground hover:text-foreground"
                          title={`Open conversation: ${tx.conversation_title ?? tx.conversation_id}`}
                          onClick={() => navigate(`/?conv=${tx.conversation_id}`)}
                        >
                          <ExternalLink className="h-3 w-3 shrink-0" />
                          <span className="max-w-[140px] truncate">
                            {tx.conversation_title ?? tx.conversation_id}
                          </span>
                        </Button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {tx.key_source ? (
                        <Badge
                          variant="outline"
                          className={`gap-1 text-xs ${
                            tx.key_source === "client"
                              ? "text-blue-700 border-blue-200 bg-blue-50"
                              : "text-gray-600 border-gray-200 bg-gray-50"
                          }`}
                        >
                          <Key className="h-3 w-3" />
                          {tx.key_source === "client" ? "Client" : APP_NAME}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono text-sm">
                      {tx.tokens_used != null ? tx.tokens_used.toLocaleString() : "—"}
                    </TableCell>
                    <TableCell
                      className={`text-right font-mono font-semibold ${
                        tx.amount < 0 ? "text-destructive" : "text-green-700"
                      }`}
                    >
                      {tx.amount > 0 ? "+" : ""}
                      {tx.amount.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </div>
    </div>
  );
};

export default WalletLedger;
